/**
 * RTWO Cryptography & Identity Engine — Service Factory
 */

import { cryptoEventBus } from '../events/crypto-event-bus';
import {
  identityBackendRegistry,
  keyProviderRegistry,
  type IdentityBackendRegistry,
  type KeyProviderRegistry,
} from '../registry/provider-registries';
import {
  createCryptoRepositories,
  type CryptoRepositories,
} from '../repositories/crypto-repositories';
import { KeyPairGenerationService } from './key-pair-generation-service';
import { AddressDerivationService } from './address-derivation-service';
import { SignatureCreationService } from './signature-creation-service';
import { SignatureVerificationService } from './signature-verification-service';
import { PublicKeyValidationService } from './public-key-validation-service';
import { IdentityRegistrationService } from './identity-registration-service';
import { IdentityVerificationService } from './identity-verification-service';
import { KeyRotationService } from './key-rotation-service';
import { KeyStoragePolicyService } from './key-storage-policy-service';
import { TrustRecordService } from './trust-record-service';
import { CryptoEventPublishingService } from './crypto-event-publishing-service';
import { ProviderLifecycleService } from './provider-lifecycle-service';
import { ProviderRegistrationService } from './provider-registration-service';
import { ConfigurationBindingService } from './configuration-binding-service';
import { RepositoryBootstrapService } from './repository-bootstrap-service';
import { registerCryptoProviderCatalog } from '../bootstrap/crypto-provider-bootstrap';
import type { CryptoServiceContext } from './base-crypto-service';

export interface CryptoEngineServices {
  keyPairGeneration: KeyPairGenerationService;
  addressDerivation: AddressDerivationService;
  signatureCreation: SignatureCreationService;
  signatureVerification: SignatureVerificationService;
  publicKeyValidation: PublicKeyValidationService;
  identityRegistration: IdentityRegistrationService;
  identityVerification: IdentityVerificationService;
  keyRotation: KeyRotationService;
  keyStoragePolicy: KeyStoragePolicyService;
  trustRecord: TrustRecordService;
  eventPublishing: CryptoEventPublishingService;
  providerLifecycle: ProviderLifecycleService;
  providerRegistration: ProviderRegistrationService;
  configurationBinding: ConfigurationBindingService;
  repositoryBootstrap: RepositoryBootstrapService;
}

export function createCryptoEngineServices(options?: {
  repositories?: CryptoRepositories;
  keyProviderRegistry?: KeyProviderRegistry;
  identityBackendRegistry?: IdentityBackendRegistry;
  eventBus?: typeof cryptoEventBus;
}): {
  repositories: CryptoRepositories;
  keyProviderRegistry: KeyProviderRegistry;
  identityBackendRegistry: IdentityBackendRegistry;
  services: CryptoEngineServices;
  context: CryptoServiceContext;
} {
  const repositories = options?.repositories ?? createCryptoRepositories();
  const keyProviders = options?.keyProviderRegistry ?? keyProviderRegistry;
  const identityBackends = options?.identityBackendRegistry ?? identityBackendRegistry;

  registerCryptoProviderCatalog(keyProviders, identityBackends);

  const context: CryptoServiceContext = {
    eventBus: options?.eventBus ?? cryptoEventBus,
  };

  const services: CryptoEngineServices = {
    keyPairGeneration: new KeyPairGenerationService(context, repositories, keyProviders),
    addressDerivation: new AddressDerivationService(context, repositories, keyProviders),
    signatureCreation: new SignatureCreationService(context, repositories, keyProviders),
    signatureVerification: new SignatureVerificationService(context, keyProviders),
    publicKeyValidation: new PublicKeyValidationService(context, keyProviders),
    identityRegistration: new IdentityRegistrationService(context, repositories, identityBackends),
    identityVerification: new IdentityVerificationService(context, repositories, identityBackends),
    keyRotation: new KeyRotationService(context, repositories, keyProviders),
    keyStoragePolicy: new KeyStoragePolicyService(context, repositories),
    trustRecord: new TrustRecordService(context, repositories, identityBackends),
    eventPublishing: new CryptoEventPublishingService(context, repositories),
    providerLifecycle: new ProviderLifecycleService(context, keyProviders, identityBackends),
    providerRegistration: new ProviderRegistrationService(context, keyProviders, identityBackends),
    configurationBinding: new ConfigurationBindingService(context, keyProviders, identityBackends),
    repositoryBootstrap: new RepositoryBootstrapService(
      context,
      keyProviders,
      identityBackends,
      repositories
    ),
  };

  return {
    repositories,
    keyProviderRegistry: keyProviders,
    identityBackendRegistry: identityBackends,
    services,
    context,
  };
}
